import {
  faChevronDown,
  faChevronRight
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Checkbox, Grid, Typography } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { useAtom } from 'jotai';
import get from 'lodash/get';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import DynamicFormControl from '@/components/DynamicFormControl';

import CategoryField from '../CategoryField/CategoryField';
import DynamicFormField from '../DynamicFormField';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import SiteNameField from '../SiteNameField/SiteNameField';
import { selectedSourceDetailsAtom } from '../SourceCreate.store';
import {
  type DataSourceRowProps,
  SourceDialogMode
} from '../SourceCreate.types';
import SourceInfoBlock from '../SourceInfoBlock/SourceInfoBlock';
import { isFieldRequired } from './DataProvider.fieldProcessing';
import useStyles from './DataProvider.styles';
import { useDataProviderFields } from './useDataProviderFields.hook';
import { useDataProviderRowState } from './useDataProviderRowState.hook';

const DataSourceRow = ({ DataProvider, ...props }: DataSourceRowProps) => {
  const { data_source, parent_name } = props;
  const { formState, control } = useFormContext();
  const { errors } = formState;
  const { classes } = useStyles();
  const [selectedSourceDetails] = useAtom(selectedSourceDetailsAtom);

  // Manage row UI state (expansion, selection, checkbox visibility)
  const {
    open,
    setOpen,
    isSelected,
    isProviderDisabled,
    shouldShowCheckbox,
    mode,
    initialSelectionRef
  } = useDataProviderRowState({
    parentName: parent_name || '',
    dataSourceName: data_source.data_source
  });

  // Process fields through validation and business rule pipeline
  const sourceInputs = useDataProviderFields({
    isSelected,
    parentName: parent_name || '',
    mode,
    sourceInputs: data_source.source_inputs,
    selectedSourceDetails,
    dataSourceName: data_source.data_source
  });

  const checkboxName = `${parent_name}.selected`;

  return (
    <>
      <TableRow>
        <TableCell
          colSpan={9}
          component="th"
          onClick={() => setOpen(!open)}
          className={classes.dsHeaderTableCell1}
        >
          <IconButton aria-label="expand row" size="small">
            {open ? (
              <FontAwesomeIcon
                icon={faChevronRight}
                style={{ fontSize: '0.9rem' }}
              />
            ) : (
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{ fontSize: '0.9rem' }}
              />
            )}
          </IconButton>
          <Typography sx={{ fontWeight: 800 }}>
            {data_source?.data_source_label || data_source?.data_source}
          </Typography>
        </TableCell>
        <TableCell className={classes.dsHeaderTableCell2} colSpan={4}>
          {shouldShowCheckbox && (
            <Controller
              name={checkboxName}
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={Boolean(field.value)}
                  onChange={(_, checked) => field.onChange(checked)}
                  size="small"
                  disabled={isProviderDisabled}
                  sx={{ float: 'right', padding: '0', ml: 1 }}
                />
              )}
            />
          )}
        </TableCell>
      </TableRow>

      {/* Collapse with inputs */}
      <TableRow className={classes.CollapseTableRow}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ flexGrow: 1, py: '1.25rem' }}>
              <Grid container spacing={2}>
                {/* Render all fields except "active" fields */}
                {sourceInputs
                  .filter((field) => !field.name.includes('active'))
                  .map((field) => {
                    // Special handling for site name field
                    if (field.name.includes('site_name')) {
                      return (
                        <React.Fragment key={field.name}>
                          <SiteNameField
                            key={field.name}
                            name={field.name}
                            srcTypeId={data_source?.source_type_id || 0}
                            dataSourceName={data_source.data_source}
                            xs={12}
                            sm={6}
                            md={6}
                          />
                        </React.Fragment>
                      );
                    }

                    // Special handling for category field
                    if (field.name.includes('dataprovider_category')) {
                      return (
                        <React.Fragment key={field.name}>
                          <CategoryField
                            key={field.name}
                            provider={parent_name || ''}
                            subProvider={data_source.data_source}
                            sourceTypeId={data_source.source_type_id ?? 0}
                            field={field}
                            initialValue={initialSelectionRef.current}
                            mode={mode as SourceDialogMode}
                          />
                        </React.Fragment>
                      );
                    }

                    // Field is already processed through all business rules via useDataProviderFields
                    return (
                      <DynamicFormField
                        key={field.name}
                        formField={field}
                        xs={12}
                        sm={6}
                        md={6}
                        isRequired={isFieldRequired(field)}
                      >
                        <DynamicFormControl {...field} name={field.name} />
                        {get(errors, field.name) && (
                          <ErrorMessage
                            message={get(errors, field.name)?.message}
                          />
                        )}
                      </DynamicFormField>
                    );
                  })}

                {/* Display source information block */}
                <SourceInfoBlock
                  sourceTypeId={data_source?.source_type_id || 0}
                  subSource={data_source?.data_source}
                />

                {/* Dedicated block for rendering "active" fields only in Edit mode */}
                {mode === SourceDialogMode.Edit &&
                  sourceInputs
                    .filter((field) => field.name.includes('active'))
                    .map((field) => (
                      <DynamicFormField
                        key={field.name}
                        formField={field}
                        xs={12}
                        sm={6}
                        md={6}
                      >
                        <DynamicFormControl {...field} name={field.name} />
                      </DynamicFormField>
                    ))}

                {/* Nested children */}
                {data_source.data_sources && (
                  <Box className={classes.nestedChild}>
                    <DataProvider
                      parent_name={parent_name}
                      data_sources={data_source.data_sources}
                    />
                  </Box>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default DataSourceRow;
